<template>
  <v-app id="console">
    <nav-drawer />
    <toolbar
      @updatePassword="showPasswordUpdate"
      @logoutClicked="logout = true"
    />
    <v-main>
      <broker-boost-banner
        v-if="isBrokerAdmin || impBrokerage || isAgent"
        :is-enabled="boostEnabled"
      />

      <v-alert
        v-model="passwordReset"
        class="mx-6 mt-6 py-1"
        type="warning"
        color="warning"
        variant="outlined"
        :icon="false"
      >
        <div class="d-flex align-center justify-space-between">
          <div>
            <v-icon icon="mdi:mdi-alert-circle" class="me-4"></v-icon>
            Your password has recently expired or has been reset. Please update
            it now.
          </div>
          <v-btn @click="showPasswordUpdate" variant="text"> Reset </v-btn>
        </div>
      </v-alert>
      <v-container :fluid="true" grid-list-sm>
        <!--              <v-container class="pa-0" fluid grid-list-sm>-->
        <!--        <transition name="slide-x-reverse-transition" mode="out-in">-->
        <router-view />
        <!--        </transition>-->
      </v-container>
    </v-main>

    <logout-dialog :dialog="logout" @logoutCancel="logout = false" />
    <update-password
      :dialog="updatePassword"
      @cancel="updatePassword = false"
      @updated="updatedPassword"
      @update:dialog="updatePassword = $event"
    />
    <stay-logged-in-dialog
      ref="stayLoggedInDialogRef"
      :dialog="stillThereDialog"
      :seconds="sessRemainingSeconds"
      @reauth="reauthUser"
      @logout="logOutUser"
      @update:dialog="stillThereDialog = $event"
    />
    <broker-boost-info-dialog
      :dialog="brokerBoostDialog"
      :is-enabled="boostEnabled"
      @cancel="brokerBoostDialog = false"
      @update:dialog="brokerBoostDialog = $event"
    />
    <v-snackbar v-model="alert" :color="alertColor" multi-line>
      {{ alertText }}
      <template v-slot:actions>
        <v-btn
          icon="$close"
          density="comfortable"
          variant="plain"
          @click="alert = false"
        ></v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import NavDrawer from "@/components/console/NavDrawer.vue";
import Toolbar from "@/components/console/Toolbar.vue";
import StayLoggedInDialog from "@/components/console/dialog/StayLoggedInDialog.vue";
import BrokerBoostBanner from "@/components/console/partials/BrokerBoostBanner";
import { defineAsyncComponent } from "vue";
import { useEventBus } from "@/event-bus/bus";
import $const from "@/utils/constants";

const SESSION_CHECK_INTERVAL = 10000;
const SESSION_ACTION_INTERVAL = 30000;
const SESSION_ACTIVITY_INTERVAL = 270000;

export default {
  name: "console",
  props: {},
  components: {
    BrokerBoostBanner,
    NavDrawer,
    Toolbar,
    StayLoggedInDialog,
    LogoutDialog: defineAsyncComponent(
      () => import("@/components/console/dialog/LogoutDialog.vue"),
    ),
    UpdatePassword: defineAsyncComponent(
      () => import("@/components/auth/PasswordUpdate.vue"),
    ),
    BrokerBoostInfoDialog: defineAsyncComponent(
      () => import("@/components/console/dialog/BrokerBoostInfoDialog"),
    ),
  },
  data() {
    return {
      // authRefreshTimer: null,
      sessionCheckInterval: null,
      sessRemainingSeconds: 0,
      drawer: false,
      logout: false,
      updatePassword: false,
      stillThereDialog: false,
      lastActive: new Date(),
      brokerBoostDialog: false,
      alert: false,
      alertText: "",
      alertColor: "success",
    };
  },
  watch: {
    sessionState: {
      handler: function () {
        console.log("session changed....");
        this.startSessionCheckTask();
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("user", [
      "me",
      "responsive",
      "isAdmin",
      "brokerBoostStatus",
      "isBrokerAdmin",
      "isAgent",
    ]),
    ...mapGetters("auth", [
      "sessionAutoRefresh",
      "session",
      "sessionExpiration",
      "impBrokerage",
    ]),
    sessionState() {
      return this.session;
    },
    passwordReset() {
      return this.me.passwordExpired;
    },
    boostEnabled() {
      // this.loadBoostStatus();
      return (
        this.brokerBoostStatus?.enabled !== null &&
        this.brokerBoostStatus?.enabled
      );
    },
  },
  methods: {
    ...mapActions("auth", ["reAuth"]),
    ...mapMutations("auth", ["setSessionAutoRefresh"]),
    showBoostDialog() {
      this.brokerBoostDialog = true;
    },
    showPasswordUpdate() {
      console.log("emit works");
      this.updatePassword = true;
    },
    updatedPassword() {
      this.updatePassword = false;
      this.alertText = "Your password has been updated.";
      this.alertColor = "success";
      this.alert = true;
    },
    startSessionCheckTask() {
      // wipe the interval if one exists
      if (this.sessionCheckInterval) {
        clearInterval(this.sessionCheckInterval);
        this.sessionCheckInterval = null;
      }

      this.sessionCheckInterval = setInterval(() => {
        const now = new Date();
        const sessExp = new Date(this.sessionExpiration);
        const expTime = sessExp.getTime() - now.getTime();

        if (expTime < SESSION_ACTION_INTERVAL) {
          clearInterval(this.sessionCheckInterval);
          this.sessionCheckInterval = null;

          // session refresh/notify window
          if (this.sessionAutoRefresh) {
            this.refreshToken();
          } else {
            // check last activity
            const lastActive = new Date().getTime() - this.lastActive.getTime();
            console.log(`lastActive: ${lastActive}`);
            if (lastActive < SESSION_ACTIVITY_INTERVAL) {
              console.log("auto refresh token");
              this.refreshToken();
            } else {
              console.log("show dialog");
              this.showStillThereDialog(expTime);
            }
          }
        }
      }, SESSION_CHECK_INTERVAL);
    },
    checkSessionAccessToken() {
      if (this.session.access_token === null) {
        // no token - no fun and games, log user out
        this.logOutUser();
      } else {
        this.startSessionCheckTask();
      }
    },
    refreshToken() {
      this.reAuth().then(
        () => {
          this.checkSessionAccessToken();
        },
        () => {
          this.checkSessionAccessToken();
        },
      );
    },
    reauthUser(stayLoggedIn) {
      this.stillThereDialog = false;
      this.refreshToken();

      if (stayLoggedIn) {
        this.setSessionAutoRefresh(stayLoggedIn);
      }
    },
    loadBoostStatus() {
      if (this.impBrokerage || this.isBrokerAdmin) {
        console.log("loadBoostStatus fired in console for impersonations");
        this.$store
          .dispatch("user/loadBoostStatus")
          .then((resp) => {
            console.log(
              "console loadBoostStatus resp: " + JSON.stringify(resp),
            );
          })
          .catch((e) => {
            console.log("console loadBoostStatus error: " + e);
          });
      }
    },
    showStillThereDialog() {
      this.sessRemainingSeconds = 30;
      this.stillThereDialog = true;
    },
    logOutUser() {
      if (this.sessionCheckInterval) {
        clearInterval(this.sessionCheckInterval);
        this.sessionCheckInterval = null;
      }

      this.$store
        .dispatch("auth/logout")
        .then(() => {
          return this.$store.dispatch("user/unloadMe");
        })
        .then(() => {
          return this.$store.dispatch("auth/clearImpersonate");
        })
        .then(() => {
          this.$router.push({ name: "login" });
        });
    },
    setActivityTracker() {
      document.onload = this.trackActivity;
      // document.onmousemove = this.trackActivity;
      document.onmousedown = this.trackActivity; // touchscreen presses
      document.ontouchstart = this.trackActivity;
      document.onclick = this.trackActivity; // touchpad clicks
      document.onkeydown = this.trackActivity; // onkeypress is deprectaed
      document.addEventListener("scroll", this.trackActivity, true); // improved; see comments
    },
    trackActivity() {
      this.lastActive = new Date();
      // console.log(`activity detected: ${this.lastActive}`);
    },
  },
  mounted() {
    this.startSessionCheckTask();

    if (this.me.passwordExpired) {
      this.showPasswordUpdate();
    }

    if (this.impBrokerage || this.isBrokerAdmin) {
      // set timer to trigger auto reload of status once a day
      // only needed if for users who stay logged in browser for a long time periods
      setInterval(this.loadBoostStatus, 1000 * 60 * 60 * 24);
      this.loadBoostStatus();
    }
    const _eventBus = useEventBus();

    _eventBus.$on($const.EVENT_BUS.UPDATE_PASSWORD, this.showPasswordUpdate);
    _eventBus.$on($const.EVENT_BUS.RELOAD_BOOST_STATUS, this.loadBoostStatus);
    _eventBus.$on(
      $const.EVENT_BUS.SHOW_BROKER_BOOST_BANNER,
      this.showBoostDialog,
    );

    // all events listener?
    this.setActivityTracker();
  },
  created() {},
  beforeUnmount() {
    // if (this.authRefreshTimer) {
    //   console.log("clearing the refresh timer...");
    //   clearInterval(this.authRefreshTimer);
    //   this.authRefreshTimer = null;
    // }
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }

    // this.$eventBus.$off(
    //   this.$const.EVENT_BUS.UPDATE_PASSWORD,
    //   this.showPasswordUpdate
    // );
    // this.$eventBus.$off(
    //   this.$const.EVENT_BUS.RELOAD_BOOST_STATUS,
    //   this.loadBoostStatus
    // );
    // this.$eventBus.$off(
    //   this.$const.EVENT_BUS.SHOW_BROKER_BOOST_BANNER,
    //   this.manageBoostDialog
    // );
  },
};
</script>

<!-- styling for the component -->
<style scoped />

<!--ps 383 384 -->
